import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { ActingAsBanner } from "@/app/layout/acting-as-banner";
import { AppFooter } from "@/app/layout/app-footer";
import { FORCED_COLORS_BACKPLATE } from "@/app/layout/forced-colors-backplate";
import { getVisibleNavigationEntries, groupNavigationEntries } from "@/app/layout/navigation";
import { MEMBER_INVOICES_ROUTE, isPathVisibleForStanding } from "@/app/layout/restricted-shell";
import { UserMenu } from "@/app/layout/user-menu";
import { useAuth } from "@/core/auth/auth-provider";
import { LegacyOwnedRejectionHost } from "@/core/write-authority/legacy-owned-rejection-host";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  NavigationItem,
  NavigationList,
  ResponsiveSidebar,
} from "@/shared/components/ui";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import type { MemberStanding } from "@/types/clm-web";

export function AppLayout(): ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const auth = useAuth();
  const standing = auth.session?.standing;
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!sidebarOpen) {
      toggleButtonRef.current?.focus();
      return;
    }

    const focusTarget = document.querySelector<HTMLElement>(
      '#app-navigation a, #app-navigation button, #app-navigation [tabindex]:not([tabindex="-1"])',
    );
    focusTarget?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sidebarOpen]);

  return (
    <ResponsiveSidebar
      open={sidebarOpen}
      sidebarId="app-navigation"
      sidebarLabel="Primary navigation"
      onBackdropClick={() => setSidebarOpen(false)}
      sidebar={
        <SidebarNavigation
          pathname={location.pathname}
          roleClaims={auth.effectiveRoleClaims}
          standing={standing}
          onNavigate={() => setSidebarOpen(false)}
        />
      }
    >
      <div className="flex min-h-screen flex-col bg-gray-50">
        {/* Gradient header/footer branding per the Spec Kit ux-design-guidelines
            (the one approved raw-hex gradient; dark end left for Tracker continuity,
            Luis 2026-10-30). */}
        <header
          role="banner"
          className="sticky top-0 z-30 bg-gradient-to-r from-[#16456B] to-[#1893D1]"
        >
          <div className="clm-page-gutter mx-auto flex h-[54px] max-w-[1200px] items-center justify-between gap-4 text-white">
            <div className="flex items-center gap-3">
              <Button
                ref={toggleButtonRef}
                type="button"
                variant="outline"
                size="icon"
                className="border-white/60 bg-transparent text-white hover:border-white/60 hover:bg-white/15 hover:text-white lg:hidden"
                aria-label="Toggle navigation"
                aria-expanded={sidebarOpen}
                aria-controls="app-navigation"
                onClick={() => setSidebarOpen((open) => !open)}
              >
                <ClmIcon name="menu" decorative />
              </Button>
              <Link
                to="/"
                className="flex items-center gap-2 no-underline hover:no-underline"
                aria-label="CLM Web home"
              >
                <img
                  src="/logos/header-logo.svg"
                  alt="CLM Web logo"
                  className={`h-[30px] w-auto ${FORCED_COLORS_BACKPLATE}`}
                  loading="eager"
                  width={74}
                  height={30}
                />
              </Link>
            </div>
            <UserMenu />
          </div>
        </header>
        <ActingAsBanner />
        <LegacyOwnedRejectionHost />
        <section className="clm-page-gutter clm-section-spacing flex-1">
          {isPathVisibleForStanding(location.pathname, standing) ? (
            <Outlet />
          ) : (
            <RestrictedSurfaceMessage />
          )}
        </section>
        <AppFooter />
      </div>
    </ResponsiveSidebar>
  );
}

/**
 * Restricted shell (006-6 FR-050): a hidden surface reached by direct
 * navigation renders this message inside the shell instead of the routed
 * view — never blank, never a redirect.
 */
function RestrictedSurfaceMessage(): ReactElement {
  return (
    <div className="mx-auto max-w-3xl">
      <Alert>
        <AlertTitle as="h1">This area isn't available yet</AlertTitle>
        <AlertDescription>
          It opens once your membership is active. You can view and pay your invoices from{" "}
          <Link to={MEMBER_INVOICES_ROUTE.path}>{MEMBER_INVOICES_ROUTE.label}</Link>.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function SidebarNavigation({
  pathname,
  roleClaims,
  standing,
  onNavigate,
}: {
  pathname: string;
  roleClaims: Parameters<typeof getVisibleNavigationEntries>[0];
  standing: MemberStanding | undefined;
  onNavigate: () => void;
}): ReactElement {
  const groupedEntries = groupNavigationEntries(getVisibleNavigationEntries(roleClaims, standing));

  return (
    <div className="flex h-full flex-col gap-6">
      {Object.entries(groupedEntries).map(([group, entries]) => (
        <NavigationList key={group} label={`${group} navigation`}>
          <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group}
          </p>
          {entries.map((entry) => (
            <NavigationItem
              key={entry.key}
              href={entry.href}
              active={entry.isActive(pathname)}
              onClick={onNavigate}
            >
              <ClmIcon name={entry.icon} decorative />
              <span>{entry.label}</span>
            </NavigationItem>
          ))}
        </NavigationList>
      ))}
    </div>
  );
}
